/**
 * ShopSchoen.CH storefront brand mark.
 *
 * Composition mirrors the operator's brief:
 *   - Elegant black shopping bag with a gold serif "S" and a
 *     minimalist Swiss mountain silhouette.
 *   - A small red-and-white Swiss flag anchor bottom-right of the bag.
 *   - The wordmark below in a serif black + gold split ("Shop" + "Schoen.CH")
 *     with the tagline "SMART SHOPPING · BETTER LIVING." underneath.
 *
 * Usage:
 *   <SchoenBrand size={44} showWordmark showTagline />   // full lockup
 *   <SchoenBrand size={36} />                            // bag only
 */
export function SchoenBrand({
  size = 44,
  showWordmark = false,
  showTagline = false,
  className = "",
}) {
  const bagId = `sch-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <span className={`inline-flex items-center gap-3 ${className}`}>
      {/* Bag mark */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        aria-hidden="true"
        className="shrink-0"
      >
        <defs>
          <linearGradient id={`${bagId}-gold`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F1D278" />
            <stop offset="55%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#A47C1E" />
          </linearGradient>
        </defs>
        {/* Handles */}
        <path
          d="M 35 32 Q 35 18 50 18 Q 65 18 65 32"
          fill="none"
          stroke="#0A0A0A"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        {/* Bag body */}
        <path
          d="M 22 32 L 78 32 L 74 82 L 26 82 Z"
          fill="#0A0A0A"
        />
        {/* Right gold trim (spine of the bag) */}
        <path
          d="M 72 32 L 76 32 L 72 82 L 68 82 Z"
          fill={`url(#${bagId}-gold)`}
          opacity="0.85"
        />
        <path
          d="M 65 32 L 66 32 L 63 82 L 62 82 Z"
          fill={`url(#${bagId}-gold)`}
          opacity="0.55"
        />
        {/* Gold serif S — hand-inked look */}
        <text
          x="47"
          y="68"
          textAnchor="middle"
          fill={`url(#${bagId}-gold)`}
          style={{
            fontFamily: '"Playfair Display", "Cormorant Garamond", "Times New Roman", serif',
            fontWeight: 700,
            fontSize: "52px",
            fontStyle: "italic",
          }}
        >
          S
        </text>
        {/* Mountain silhouette at the base */}
        <path
          d="M 26 82 L 38 66 L 46 74 L 55 60 L 68 78 L 74 82 Z"
          fill={`url(#${bagId}-gold)`}
          opacity="0.9"
        />
        {/* Swiss flag */}
        <rect x="63" y="70" width="12" height="12" fill="#DA291C" rx="0.5" />
        <rect x="68.6" y="72.5" width="1.8" height="7" fill="#fff" />
        <rect x="65.5" y="75.1" width="7" height="1.8" fill="#fff" />
      </svg>

      {showWordmark && (
        <span className="flex flex-col leading-none">
          <span className="flex items-baseline gap-1">
            <span
              className="text-[0.95em] font-normal tracking-tight"
              style={{
                fontFamily: '"Playfair Display", "Cormorant Garamond", "Times New Roman", serif',
                color: "var(--ss-ink, #0A0A0A)",
                fontSize: `${size * 0.42}px`,
              }}
            >
              Shop
            </span>
            <span
              className="tracking-tight"
              style={{
                fontFamily: '"Playfair Display", "Cormorant Garamond", "Times New Roman", serif',
                color: "var(--ss-gold, #B8892F)",
                fontSize: `${size * 0.42}px`,
              }}
            >
              Schoen<span style={{ color: "var(--ss-ink, #0A0A0A)" }}>.ch</span>
            </span>
          </span>
          {showTagline && (
            <span
              className="font-mono uppercase tracking-[0.22em] mt-1"
              style={{
                fontSize: `${Math.max(size * 0.14, 9)}px`,
                color: "var(--ss-ink-muted, #6B6B6B)",
              }}
            >
              Smart shopping · Better living
            </span>
          )}
        </span>
      )}
    </span>
  );
}
